/**
 * useOfflineStorage — IndexedDB safety net for audio recordings
 *
 * Saves audio blobs to IndexedDB immediately after recording stops,
 * BEFORE attempting to upload. This prevents data loss if:
 *   - The phone loses signal in the exam room
 *   - The app crashes or is force-closed
 *   - The backend is unreachable
 *
 * On app load, checks for orphaned recordings and exposes them
 * so the app can prompt the user to retry upload.
 *
 * Usage:
 *   const { saveRecording, getOrphanedRecordings, clearRecording } = useOfflineStorage();
 *
 *   // After recording stops, before upload:
 *   const recordingId = await saveRecording(audioBlob, metadata);
 *
 *   // After successful upload:
 *   await clearRecording(recordingId);
 *
 *   // On app mount, check for leftovers:
 *   const orphans = await getOrphanedRecordings();
 */

import { useCallback, useEffect, useState } from 'react';
import { formatLocalYMD } from '../utils/dateUtils';

const DB_NAME = 'clarime-offline';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';

// ── IndexedDB helpers ────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function dbPut(record) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function dbGet(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function dbDelete(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function dbGetAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

// ── Cross-instance sync ──────────────────────────────────────────────
//
// Multiple components hold their own useOfflineStorage() instance (App's
// home banner, ProcessingAndSummaryScreen's save/clear). Each instance
// keeps orphan state in React state, so a deletion in one instance used to
// leave every OTHER instance stale — the home banner kept offering "Retry"
// on a recording that had already uploaded and been cleared. Every mutation
// now announces itself, and every instance reloads from IndexedDB (the
// source of truth) when it hears the announcement.

const ORPHANS_CHANGED_EVENT = 'clarime:orphans-changed';

function notifyOrphansChanged() {
  window.dispatchEvent(new CustomEvent(ORPHANS_CHANGED_EVENT));
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useOfflineStorage() {
  const [orphanedRecordings, setOrphanedRecordings] = useState([]);
  const [isChecking, setIsChecking] = useState(true);

  // Load orphans on mount, and reload whenever any instance mutates the DB
  useEffect(() => {
    let cancelled = false;

    async function checkOrphans() {
      try {
        const all = await dbGetAll();
        // Recordings that were saved but never uploaded (or upload failed)
        const orphans = all.filter(r => r.status === 'pending' || r.status === 'failed');

        // Auto-clean recordings older than 7 days to prevent IndexedDB bloat
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const expired = all.filter(r => r.createdAt < sevenDaysAgo);
        for (const rec of expired) {
          await dbDelete(rec.id);
        }

        if (!cancelled) {
          setOrphanedRecordings(orphans.filter(r => r.createdAt >= sevenDaysAgo));
        }
      } catch (err) {
        console.warn('Could not check offline recordings:', err.message);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    }

    checkOrphans();
    window.addEventListener(ORPHANS_CHANGED_EVENT, checkOrphans);
    return () => {
      cancelled = true;
      window.removeEventListener(ORPHANS_CHANGED_EVENT, checkOrphans);
    };
  }, []);

  /**
   * Save an audio blob to IndexedDB immediately after recording.
   * Returns a unique recording ID that should be passed to clearRecording
   * after a successful backend upload.
   */
  const saveRecording = useCallback(async (audioBlob, metadata = {}) => {
    const id = `rec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const record = {
      id,
      audioBlob,
      mimeType: audioBlob.type,
      size: audioBlob.size,
      createdAt: Date.now(),
      status: 'pending', // pending → uploaded → cleared (or failed)
      metadata: {
        durationSeconds: metadata.durationSeconds || 0,
        visitDate: metadata.visitDate || formatLocalYMD(),
        visitTime: metadata.visitTime || new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        ...metadata,
      },
    };

    await dbPut(record);
    notifyOrphansChanged();
    return id;
  }, []);

  /**
   * Mark a recording as successfully uploaded. Deletes the blob from IndexedDB.
   */
  const clearRecording = useCallback(async (recordingId) => {
    try {
      await dbDelete(recordingId);
      notifyOrphansChanged();
    } catch (err) {
      console.warn('Could not clear offline recording:', err.message);
    }
  }, []);

  /**
   * Mark a recording as failed (will show up as orphan on next load).
   */
  const markFailed = useCallback(async (recordingId) => {
    try {
      const record = await dbGet(recordingId);
      if (record) {
        record.status = 'failed';
        record.failedAt = Date.now();
        await dbPut(record);
        notifyOrphansChanged();
      }
    } catch (err) {
      console.warn('Could not mark recording as failed:', err.message);
    }
  }, []);

  /**
   * Dismiss/delete an orphaned recording (user chose not to retry).
   */
  const dismissRecording = useCallback(async (recordingId) => {
    await dbDelete(recordingId);
    notifyOrphansChanged();
  }, []);

  return {
    /** Save audio blob to IndexedDB. Returns recordingId. */
    saveRecording,
    /** Delete recording from IndexedDB after successful upload. */
    clearRecording,
    /** Mark recording as failed upload (keeps blob for retry). */
    markFailed,
    /** Dismiss orphaned recording (deletes blob). */
    dismissRecording,
    /** Recordings that were saved but never successfully uploaded. */
    orphanedRecordings,
    /** True while checking IndexedDB on mount. */
    isChecking,
  };
}
